import React from "react"
import {Box, CircularProgress, Typography} from "@material-ui/core"

import {CreateOfferButton} from "./CreateOfferButton"
import {OfferCard} from "./OfferCard"

import {api, CustomerOffer} from "@indebted/api"
import {useNotification} from "@indebted/components/Notification"

type CustomerOffersProps = {
	customerID: string
	reload: () => void
	enableOneOffOffers?: boolean
	enablePlanOffers?: boolean
	minInstalmentAmount?: number
}

function CustomerOffers({
	customerID,
	reload,
	enableOneOffOffers = true,
	enablePlanOffers = false,
	minInstalmentAmount = 10,
}: CustomerOffersProps) {
	const {notification} = useNotification()
	const [activeOffers, setActiveOffers] = React.useState<CustomerOffer[]>([])
	const [inactiveOffers, setInactiveOffers] = React.useState<CustomerOffer[]>([])
	const [loading, setLoading] = React.useState(true)

	const loadOffers = React.useCallback(async () => {
		setLoading(true)
		try {
			const response = await api.customer.getOffers(customerID)
			setActiveOffers(response.ActiveOffers || [])
			setInactiveOffers(response.InactiveOffers || [])
		} catch (error: any) {
			notification.error(error.Message || "Failed to load offers")
		} finally {
			setLoading(false)
		}
	}, [customerID, notification])

	React.useEffect(() => {
		loadOffers()
	}, [loadOffers])

	const handleRevokeSuccess = async () => {
		await loadOffers()
		reload()
	}

	const handleCreateSuccess = async () => {
		await loadOffers()
		reload()
	}

	return (
		<Box p={3}>
			{(enableOneOffOffers || enablePlanOffers) && (
				<Box display="flex" justifyContent="flex-end" mb={2}>
					<CreateOfferButton
						customerID={customerID}
						onSuccess={handleCreateSuccess}
						enableOneOffOffers={enableOneOffOffers}
						enablePlanOffers={enablePlanOffers}
						minInstalmentAmount={minInstalmentAmount}
					/>
				</Box>
			)}

			{loading ? (
				<Box display="flex" justifyContent="center" p={4}>
					<CircularProgress />
				</Box>
			) : activeOffers.length === 0 && inactiveOffers.length === 0 ? (
				<Box p={4} textAlign="center">
					<Typography variant="body1" color="textSecondary">
						No offers found for this customer
					</Typography>
				</Box>
			) : (
				<Box display="flex" flexDirection="column" style={{gap: "40px"}}>
					{activeOffers.length > 0 && (
						<Box>
							<Box mb={2} pb={0.5} borderBottom="1px solid #e0e0e0">
								<Typography variant="subtitle1" style={{fontWeight: 500, color: "#2e7d32"}}>
									Available Offers
								</Typography>
							</Box>
							<Box display="flex" flexDirection="column" style={{gap: "16px"}}>
								{activeOffers.map((offer) => (
									<OfferCard
										key={offer.OfferID}
										offer={offer}
										customerID={customerID}
										onRevokeSuccess={handleRevokeSuccess}
									/>
								))}
							</Box>
						</Box>
					)}

					{inactiveOffers.length > 0 && (
						<Box>
							<Box mb={2} pb={0.5} borderBottom="1px solid #e0e0e0">
								<Typography variant="subtitle1" style={{fontWeight: 500, color: "#757575"}}>
									Offers History
								</Typography>
							</Box>
							<Box display="flex" flexDirection="column" style={{gap: "16px"}}>
								{inactiveOffers.map((offer) => (
									<OfferCard
										key={offer.OfferID}
										offer={offer}
										customerID={customerID}
										onRevokeSuccess={handleRevokeSuccess}
									/>
								))}
							</Box>
						</Box>
					)}
				</Box>
			)}
		</Box>
	)
}

export {CustomerOffers}
